import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["heading", "emailCount", "progress"]
    static values = {
        statusUrl: String,
        dashboardUrl: String,
        hasMessages: String
    }

    connect() {
        this.attempts = 0
        this.maxAttempts = 15 // 15 attempts * 2 seconds = 30 seconds max wait
        this.pollInterval = 2000 // 2 seconds
        this.minDisplayTime = 10000 // Minimum 10 seconds on this page
        this.startTime = Date.now()
        this.ready = false
        this.count = 0
        this.connected = false

        // Start with 5% progress
        this.updateProgress(5)

        // Start progress animation (fills over minDisplayTime)
        this.startProgressAnimation()

        // Start polling
        this.poll()
    }

    disconnect() {
        if (this.timeout) {
            clearTimeout(this.timeout)
        }
        if (this.progressInterval) {
            clearInterval(this.progressInterval)
        }
    }

    startProgressAnimation() {
        // Smoothly fill progress bar over the minimum display time
        this.progressInterval = setInterval(() => {
            const elapsed = Date.now() - this.startTime
            const progress = Math.min(95, 5 + (elapsed / this.minDisplayTime) * 90)
            this.updateProgress(progress)

            // When minimum time is reached and we're ready, complete and redirect
            if (elapsed >= this.minDisplayTime) {
                clearInterval(this.progressInterval)
                if (this.ready) {
                    this.completeAndRedirect()
                }
            }
        }, 100)
    }

    async poll() {
        this.attempts++

        try {
            const response = await fetch(this.statusUrlValue)
            const data = await response.json()

            if (data.ready || data.count > 0) {
                // We're connected once we have any emails
                if (!this.connected) {
                    this.connected = true
                    this.updateHeading("Connected!")
                }

                this.count = data.count
                this.updateEmailCount(data.count)

                if (data.ready) {
                    this.ready = true

                    // Check if minimum time has passed
                    const elapsed = Date.now() - this.startTime
                    if (elapsed >= this.minDisplayTime) {
                        this.completeAndRedirect()
                        return
                    }
                }

                // Keep polling to update count until minimum time passes
                this.timeout = setTimeout(() => this.poll(), this.pollInterval)
                return
            }

        } catch (error) {
            console.error("Setup poll error:", error)
        }

        // Continue polling or give up
        if (this.attempts < this.maxAttempts) {
            this.timeout = setTimeout(() => this.poll(), this.pollInterval)
        } else {
            // Max attempts reached, redirect anyway after minimum time
            this.ready = true
            this.updateHeading("Redirecting…")
            const elapsed = Date.now() - this.startTime
            if (elapsed >= this.minDisplayTime) {
                this.completeAndRedirect()
            }
        }
    }

    completeAndRedirect() {
        this.updateProgress(100)
        this.updateHeading("All set!")
        setTimeout(() => this.redirectToDashboard(), 800)
    }

    updateHeading(message) {
        if (this.hasHeadingTarget) {
            this.headingTarget.textContent = message
        }
    }

    updateEmailCount(count) {
        if (this.hasEmailCountTarget) {
            if (count === 1) {
                this.emailCountTarget.textContent = `${count} email synced`
            } else {
                this.emailCountTarget.textContent = `${count} emails synced`
            }
        }
    }

    updateProgress(percent) {
        if (this.hasProgressTarget) {
            this.progressTarget.style.width = `${percent}%`
        }
    }

    redirectToDashboard() {
        window.location.href = this.dashboardUrlValue
    }
}

